import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { PhxToastMetaData, PhxToastOptions } from './phx-toast.types';
import { animate, AnimationEvent, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'phx-toast',
  templateUrl: './phx-toast.component.html',
  host: { class: 'phx-toast' },
  animations: [
    trigger('moveUpMotion', [
      transition('* => enter', [
        style({
          transformOrigin: '0 0',
          transform: 'translateY(-100%)',
          opacity: 0,
        }),
        animate(
          '0.1s',
          style({
            transformOrigin: '0 0',
            transform: 'translateY(0%)',
            opacity: 1,
          })
        ),
      ]),
      transition('* => leave', [
        style({
          transformOrigin: '0 0',
          transform: 'translateY(0%)',
          opacity: 1,
        }),
        animate(
          '0.1s',
          style({
            transformOrigin: '0 0',
            transform: 'translateY(-100%)',
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
})
export class PhxToastComponent implements OnInit, OnChanges, OnDestroy {
  @Input() phxToastInstance: PhxToastMetaData;
  @Output() readonly phxToastDestroyed = new EventEmitter<{ id: string; userAction: boolean }>();
  public isContentTemplate = false;

  private _options!: PhxToastOptions;
  private _autoClose?: boolean;
  private _eraseTimer: number | null = null;
  private _eraseTimingStart?: number;
  private _eraseTTL!: number;
  private _userAction = false;

  constructor(private _cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this._options = this.phxToastInstance.options;

    this.phxToastInstance.state = 'enter';

    this._autoClose = this._options.phxDuration > 0;

    if (this._autoClose) {
      this.initErase();
      this.startEraseTimeout();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxToastInstance } = changes;

    if (phxToastInstance) {
      this.isContentTemplate = this.phxToastInstance.toastData.content instanceof TemplateRef;
    }
  }

  public ngOnDestroy(): void {
    if (this._autoClose) {
      this.clearEraseTimeout();
    }
  }

  public onEnter(): void {
    if (this._autoClose && this._options.phxPauseOnHover) {
      this.clearEraseTimeout();
      this.updateTTL();
    }
  }

  public onLeave(): void {
    if (this._autoClose && this._options.phxPauseOnHover) {
      this.startEraseTimeout();
    }
  }

  public onCloseButtonClicked(): void {
    this._userAction = true;
    this.destroy();
  }

  public onAnimationEnd(event: AnimationEvent): void {
    if (event.toState === 'leave') {
      this.phxToastDestroyed.next({ id: this.phxToastInstance.toastId, userAction: this._userAction });
    }
  }

  private destroy(): void {
    this.phxToastInstance.state = 'leave';
    this._cdr.detectChanges();
  }

  private initErase(): void {
    this._eraseTTL = this._options.phxDuration;
    this._eraseTimingStart = Date.now();
  }

  private updateTTL(): void {
    this._eraseTTL -= Date.now() - this._eraseTimingStart!;
  }

  private startEraseTimeout(): void {
    if (this._eraseTTL > 0) {
      this.clearEraseTimeout();
      this._eraseTimer = window.setTimeout(() => this.destroy(), this._eraseTTL);
      this._eraseTimingStart = Date.now();
    } else {
      this.destroy();
    }
  }

  private clearEraseTimeout(): void {
    if (this._eraseTimer !== null) {
      clearTimeout(this._eraseTimer);
      this._eraseTimer = null;
    }
  }
}
